'use client';

import { useEffect, useRef } from 'react';

import { styles } from './UiKitCollapseBase.styles';

export interface UiKitCollapseBaseProps {
  className?: string;
  children?: React.ReactNode;
  collapse?: boolean;
  /** Скрыть текстовый контент на количество строк */
  lineClamp?: number;
  onClick?: () => void;
  onCheckLineClampActive?: (active: boolean) => void;
  onTransitionEnd?: () => void;
}

/**
 * Компонент для плавного скрытия строчного или блочного контента
 *
 * **Важно!** В children нужно прокинуть 1 элемент
 *
 * Чтобы отключить анимацию передайте класс !max-h-max
 */
export function UiKitCollapseBase({
  className,
  children,
  collapse,
  lineClamp = 0,
  onClick,
  onCheckLineClampActive,
  onTransitionEnd,
  ...props
}: UiKitCollapseBaseProps) {
  const slots = styles({
    collapsed: collapse && lineClamp === 0,
    withLineClamp: lineClamp > 0
  });

  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const inner = root.firstElementChild;
    if (!inner) return;

    if (lineClamp > 0 && collapse) {
      const innerHeight = inner.getBoundingClientRect().height;
      const lineHeight = Number(
        getComputedStyle(inner).lineHeight.replace('px', '') || 0
      );
      const lines = Math.round(innerHeight / lineHeight);
      const isClampActivate = lines > lineClamp;

      if (onCheckLineClampActive) {
        onCheckLineClampActive(isClampActivate);
      }

      if (!isClampActivate) {
        return;
      }

      root.style.maxHeight = `${lineHeight * lineClamp}px`;

      const transitionDuration =
        Number(
          getComputedStyle(root).transitionDuration.replace('s', '') || 0.15
        ) * 1000;
      const timer = window.setTimeout(() => {
        root.style.setProperty('--line-clamp', `${lineClamp}`);
        window.requestAnimationFrame(() => {
          /**
           * TODO: Вычислять maxHeight точнее и убрать requestAnimationFrame.
           * Если внутри текста разного размера - будут скачки
           * inner.getBoundingClientRect, после применения line-clamp, отдает корректную высоту
           */
          root.style.maxHeight = `${inner.getBoundingClientRect().height}px`;
        });
      }, transitionDuration);
      return () => {
        clearTimeout(timer);
      };
    }

    if (lineClamp === 0 && collapse) {
      root.style.maxHeight = '0px';
      return;
    }

    if (!collapse && getComputedStyle(inner).display !== 'none') {
      root.style.setProperty('--line-clamp', 'none');
      window.requestAnimationFrame(() => {
        root.style.maxHeight = `${inner.getBoundingClientRect().height}px`;
      });
      const resizeHandle = () => {
        root.style.maxHeight = `${inner.getBoundingClientRect().height}px`;
      };
      window.addEventListener('resize', resizeHandle);
      return () => {
        window.removeEventListener('resize', resizeHandle);
      };
    }
  }, [collapse, lineClamp, onCheckLineClampActive, children]);

  return (
    <div
      ref={rootRef}
      className={slots.base({ className })}
      onClick={onClick}
      onTransitionEnd={onTransitionEnd}
      {...props}
    >
      {children}
    </div>
  );
}
